import { useState } from 'react'
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Copy,
  Trash2,
  ExternalLink,
  Package,
  Tag,
  Calendar,
  User
} from 'lucide-react'
import { useLibraryStore, CustomEquipment } from '@/stores/libraryStore'
import { Equipment } from '@/types'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { CreateEquipmentModal } from './CreateEquipmentModal'
import { EditEquipmentModal } from './EditEquipmentModal'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

export function EquipmentLibraryView() {
  const {
    filteredEquipment,
    equipmentLoading,
    equipmentFilters,
    setEquipmentFilters,
    setSelectedEquipment,
    setIsCreateEquipmentModalOpen,
    setIsEditModalOpen,
    deleteEquipment,
    duplicateEquipment
  } = useLibraryStore()

  const [showFilters, setShowFilters] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const handleSearch = (query: string) => {
    setEquipmentFilters({ search: query })
  }

  const handleEquipmentAction = (equipment: CustomEquipment, action: string) => {
    switch (action) {
      case 'edit':
        setSelectedEquipment(equipment)
        setIsEditModalOpen(true)
        break
      case 'duplicate':
        duplicateEquipment(equipment.id)
        toast.success('Equipment duplicated successfully')
        break
      case 'delete':
        if (window.confirm('Are you sure you want to delete this equipment?')) {
          deleteEquipment(equipment.id)
          toast.success('Equipment deleted successfully')
        }
        break
      case 'view-purchase':
        if (equipment.purchaseUrl) {
          window.open(equipment.purchaseUrl, '_blank')
        }
        break
    }
  }

  const equipmentTypeOptions: Equipment[] = [
    'bodyweight', 'dumbbells', 'barbell', 'kettlebell', 'resistance-bands',
    'pull-up-bar', 'bench', 'cable-machine', 'treadmill', 'bike', 'rowing-machine',
    'yoga-mat', 'foam-roller', 'medicine-ball', 'suspension-trainer'
  ]

  if (equipmentLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search equipment..."
              value={equipmentFilters.search || ''}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex border border-gray-300 rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm ${
                viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm border-l border-gray-300 ${
                viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              List
            </button>
          </div>

          <button
            onClick={() => setIsCreateEquipmentModalOpen(true)}
            className="coach-button-primary px-4 py-2 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Equipment
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Equipment Type
              </label>
              <select
                value={equipmentFilters.equipment || ''}
                onChange={(e) => setEquipmentFilters({ equipment: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {equipmentTypeOptions.map(type => (
                  <option key={type} value={type}>
                    {type.replace('-', ' ').charAt(0).toUpperCase() + type.replace('-', ' ').slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <select
                value={equipmentFilters.isCustom !== undefined ? equipmentFilters.isCustom.toString() : ''}
                onChange={(e) => setEquipmentFilters({ 
                  isCustom: e.target.value === '' ? undefined : e.target.value === 'true' 
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Sources</option>
                <option value="true">Custom</option>
                <option value="false">Built-in</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setEquipmentFilters({})}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Equipment Grid/List */}
      {filteredEquipment.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Package className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No equipment found</h3>
          <p className="text-gray-600 mb-4">
            {equipmentFilters.search || Object.keys(equipmentFilters).length > 0
              ? 'Try adjusting your search or filters'
              : 'Add your first piece of equipment to get started'
            }
          </p>
          <button
            onClick={() => setIsCreateEquipmentModalOpen(true)}
            className="coach-button-primary px-4 py-2"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Equipment
          </button>
        </div>
      ) : (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
          : 'space-y-4'
        }>
          {filteredEquipment.map((equipment) => (
            <div
              key={equipment.id}
              className={`coach-section hover:shadow-md transition-shadow ${
                viewMode === 'list' ? 'flex items-center gap-4' : ''
              }`}
            >
              {/* Equipment Image/Icon */}
              <div className={`${viewMode === 'list' ? 'flex-shrink-0' : 'mb-4'}`}>
                {equipment.imageUrl ? (
                  <img
                    src={equipment.imageUrl}
                    alt={equipment.name}
                    className={`${
                      viewMode === 'list' ? 'w-16 h-16' : 'w-full h-40'
                    } object-cover rounded-lg`}
                  />
                ) : (
                  <div className={`${
                    viewMode === 'list' ? 'w-16 h-16' : 'w-full h-40'
                  } bg-gray-100 rounded-lg flex items-center justify-center`}>
                    <Package className={`${
                      viewMode === 'list' ? 'w-8 h-8' : 'w-12 h-12'
                    } text-gray-400`} />
                  </div>
                )}
              </div>

              {/* Equipment Info */}
              <div className={`${viewMode === 'list' ? 'flex-1' : ''}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{equipment.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                        {equipment.type.replace('-', ' ')}
                      </span>
                      {equipment.isCustom && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                          Custom
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="relative group">
                    <button className="p-1 text-gray-400 hover:text-gray-600">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                    <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                      <div className="py-1">
                        <button
                          onClick={() => handleEquipmentAction(equipment, 'edit')}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleEquipmentAction(equipment, 'duplicate')}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </button>
                        {equipment.purchaseUrl && (
                          <button
                            onClick={() => handleEquipmentAction(equipment, 'view-purchase')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View Purchase
                          </button>
                        )}
                        <button
                          onClick={() => handleEquipmentAction(equipment, 'delete')}
                          className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Equipment Details */}
                <div className="space-y-2 text-sm text-gray-600">
                  {equipment.description && (
                    <p className="text-gray-700 line-clamp-2">{equipment.description}</p>
                  )}

                  <div className="space-y-1">
                    {equipment.brand && (
                      <div className="flex items-center">
                        <span className="text-xs font-medium">Brand:</span>
                        <span className="ml-1">{equipment.brand}</span>
                        {equipment.model && <span className="ml-1">({equipment.model})</span>}
                      </div>
                    )}

                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      Added {format(new Date(equipment.createdAt), 'MMM d, yyyy')}
                    </div>

                    {equipment.createdBy && (
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        {equipment.isCustom ? 'Custom' : 'Built-in'}
                      </div>
                    )}
                  </div>

                  {equipment.tags && equipment.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {equipment.tags.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded flex items-center"
                        >
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                      {equipment.tags.length > 3 && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                          +{equipment.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons for List View */}
                {viewMode === 'list' && (
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => handleEquipmentAction(equipment, 'edit')}
                      className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      Edit
                    </button>
                    {equipment.purchaseUrl && (
                      <button
                        onClick={() => handleEquipmentAction(equipment, 'view-purchase')}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Purchase
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <CreateEquipmentModal />
      <EditEquipmentModal />
    </div>
  )
}